import { Component, Output, EventEmitter, Input } from '@angular/core';

interface ScheduleRecord { 
  id: number | null;
  day: string; 
  startHour: number; 
  endHour: number; 
  cutoff?: number; 
  editing?: boolean; 
}

@Component({ 
  selector: 'app-weekly-schedule-editor', 
  templateUrl: './weekly-schedule-editor.component.html', 
  styleUrls: ['./weekly-schedule-editor.component.css'] 
}) 
export class WeeklyScheduleEditorComponent {
  @Input() set schedulesInput(value: ScheduleRecord[]) { this.schedules = value ? [...value] : []; }

  days: string[] = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']; 
  hours: number[] = Array.from({ length: 24 }, (_, i) => i);

  schedules: ScheduleRecord[] = []; 
  selecting = false; 
  selectionDay: string | null = null; 
  selectionStartHour: number | null = null; 
  selectionEndHour: number | null = null;

  resizingSchedule: ScheduleRecord | null = null;
  resizingEdge: 'top' | 'bottom' | null = null;

  @Output() scheduleAdd = new EventEmitter<ScheduleRecord>();
  @Output() scheduleChange = new EventEmitter<ScheduleRecord>();
  @Output() scheduleDelete = new EventEmitter<ScheduleRecord>();
  @Output() clearSchedules = new EventEmitter<void>();

  requestClearSchedules(event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();

    if (!this.schedules.length) {
      return;
    }

    if (window.confirm('Are you sure you want to clear all schedules?')) {
      this.clearSchedules.emit();
    }
  }

  cellClicked(day: string, hour: number, event: MouseEvent) {
    event.preventDefault();
    const schedule = this.schedules.find(s => s.day === day && hour >= s.startHour && hour <= s.endHour); 
    if (schedule) { 
      schedule.editing = true; 
      return;
    }
    if (!this.selecting) {
      this.selecting = true;
      this.selectionDay = day;
      this.selectionStartHour = hour;
      this.selectionEndHour = hour;
    } else if (this.selecting && day === this.selectionDay) {
      this.selectionEndHour = hour;
      this.selecting = false;
      const start = Math.min(this.selectionStartHour!, this.selectionEndHour!);
      const end = Math.max(this.selectionStartHour!, this.selectionEndHour!);
      const schedule = {
        id: null,
        day: this.selectionDay!,
        startHour: start,
        endHour: end,
        cutoff: 0
      };
      this.schedules.push(schedule);
      this.scheduleAdd.emit(schedule);
      this.selectionDay = null;
      this.selectionStartHour = null;
      this.selectionEndHour = null;
    } else {
      this.selectionDay = day;
      this.selectionStartHour = hour;
      this.selectionEndHour = hour;
    }
  }

  isCellInSelection(day: string, hour: number): boolean { 
    if (this.selecting && day === this.selectionDay && this.selectionStartHour !== null && this.selectionEndHour !== null) { 
      const start = Math.min(this.selectionStartHour, this.selectionEndHour); 
      const end = Math.max(this.selectionStartHour, this.selectionEndHour); 
      return hour >= start && hour <= end; 
    } 
    return this.schedules.some(s => s.day === day && hour >= s.startHour && hour <= s.endHour); 
  }

  isCellCoveredByRowspan(day: string, hour: number): boolean { 
    return this.schedules.some(s => s.day === day && hour > s.startHour && hour <= s.endHour ); 
  }

  isCellScheduleBorder(day: string, hour: number): boolean {
    const schedule = this.schedules.find(s => s.day === day && hour >= s.startHour && hour <= s.endHour); 
    if (!schedule) return false; 
    return (hour === schedule.startHour || hour === schedule.endHour);
  }

  getScheduleForCell(day: string, hour: number): ScheduleRecord | undefined { 
    return this.schedules.find(s => s.day === day && hour >= s.startHour && hour <= s.endHour); 
  }

  saveSchedule(schedule: ScheduleRecord) { 
    schedule.editing = false; 
    if (schedule.id == null){
      this.scheduleAdd.emit(schedule); 
    } else {
      this.scheduleChange.emit(schedule); 
    }
  }

  cancelEdit(schedule: ScheduleRecord) { 
    schedule.editing = false; 
  }

  deleteSchedule(schedule: ScheduleRecord) {
    this.schedules = this.schedules.filter(s => s !== schedule);
    this.scheduleDelete.emit(schedule);
  }

  copySchedule(schedule: ScheduleRecord) {
    this.days.forEach(day => {
      if (day === schedule.day) return;
      const conflict = this.schedules.find(s => s.day === day &&
        ((schedule.startHour >= s.startHour && schedule.startHour <= s.endHour) ||
         (schedule.endHour >= s.startHour && schedule.endHour <= s.endHour) ||
         (schedule.startHour <= s.startHour && schedule.endHour >= s.endHour)));
      if (!conflict) {
        const newSchedule = { ...schedule, day: day, id: null };
        this.schedules.push(newSchedule);
        this.scheduleAdd.emit(newSchedule);
      } 
    });
  }

  startResize(schedule: ScheduleRecord, edge: 'top' | 'bottom', event: MouseEvent) {
    event.stopPropagation();
    this.resizingSchedule = schedule;
    this.resizingEdge = edge;
    document.addEventListener('mousemove', this.onResizeMove);
    document.addEventListener('mouseup', this.onResizeEnd);
  }

  onResizeMove = (event: MouseEvent) => {
    if (!this.resizingSchedule || !this.resizingEdge) return;
    const table = document.querySelector('.schedule-table') as HTMLTableElement;
    if (!table) return;
    const timeLabels = Array.from(table.querySelectorAll('.time-label')) as HTMLElement[];
    const hoveredRow = timeLabels.findIndex(label => {
      const rect = label.getBoundingClientRect();
      return event.clientY >= rect.top && event.clientY <= rect.bottom;
    });
    if (hoveredRow === -1) return;
    const hour = this.hours[hoveredRow];
    if (this.resizingEdge === 'top') {
      if (hour < this.resizingSchedule.endHour) {
        this.resizingSchedule.startHour = hour;
      }
    } else if (hour > this.resizingSchedule.startHour) {
      this.resizingSchedule.endHour = hour;
    }
  };

  onResizeEnd = (event: MouseEvent) => {
    if (this.resizingSchedule) {
      //this.scheduleChange.emit(this.schedule);
    }
    this.resizingSchedule = null;
    this.resizingEdge = null;
    document.removeEventListener('mousemove', this.onResizeMove);
    document.removeEventListener('mouseup', this.onResizeEnd);
  };
}